import { mediaQueryLarge } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list.
 * Grid-density switching is intentionally instant on mobile/desktop:
 * no View Transition snapshot or animation is used.
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the product grid layout immediately.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    const value = target.value;

    // Make the control feel responsive immediately.
    target.checked = true;

    // Change the grid in the same frame. Do not wait for View Transitions.
    this.#setLayout(value);

    // Persist the preference after the visual update so storage never delays tap feedback.
    window.setTimeout(() => {
      try {
        const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
        sessionStorage.setItem(`product-grid-view-${viewport}`, value);
      } catch (error) {
        // Storage can be unavailable in some privacy modes; layout still works.
      }
    }, 0);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles switching between mobile and desktop breakpoints.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
